import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function AlumniCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      name: "",
      batch: "",
      job: "",
      highlight: "",
      wordings: "",
      photo: null,
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      batch: Yup.string().required("Batch is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      photo: isEdit ? Yup.mixed().nullable() : Yup.mixed().required("Photo is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("batch", values.batch);
      formData.append("job", values.job);
      formData.append("highlight", values.highlight);
      formData.append("wordings", values.wordings);
      formData.append("sort_order", values.sort_order);
      formData.append("status", values.isActive === "Active" ? "true" : "false");
      if (values.photo) {
        formData.append("photo", values.photo);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("alumni/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("alumni/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "Alumni updated successfully!" : "Alumni created successfully!");
          navigate("/admin/alumni");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`alumni/${id}`);
          const item = response.data.data;
          formik.setValues({
            name: item.name || "",
            batch: item.batch || "",
            job: item.job || "",
            highlight: item.highlight || "",
            wordings: item.wordings || "",
            photo: null,
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setEditPhotoPreview(imagepathReturn(item.photo || null));
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Alumni" : "Create New Alumni"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/alumni"),
              }}
              primaryButton={{
                label: isEdit ? "Update Alumni" : "Create Alumni",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="name" label="Name" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="batch" label="Batch" type="text" formik={formik} placeholder="e.g. 2015" />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField name="job" label="Job" type="text" formik={formik} placeholder="e.g. Software Engineer" />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="highlight"
                    label="Highlight"
                    type="text"
                    formik={formik}
                    placeholder="e.g. Healthcare"
                  />
                </Box>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="wordings"
                    label="Wordings"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="photo"
                    formik={formik}
                    label="Upload Photo"
                    editImagePreview={editPhotoPreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}